import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { PageHeader } from "@/components/common/PageHeader";
import { SurfaceCard } from "@/components/common/Cards";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Skeleton } from "@/components/ui/skeleton";
import { Check, ArrowRight, ArrowLeft, Sparkles, Loader2 } from "lucide-react";
import { cn } from "@/lib/utils";
import { toast } from "sonner";

const STEPS = [
  { key: "chief_complaint", label: "Chief Complaint", placeholder: "Patient's primary concern..." },
  { key: "medical_history", label: "Medical History", placeholder: "Relevant medical history, medications, conditions..." },
  { key: "findings", label: "Clinical Findings", placeholder: "Examination findings, imaging observations..." },
  { key: "diagnosis", label: "Diagnosis", placeholder: "Clinical diagnosis..." },
  { key: "treatment_plan", label: "Treatment Plan", placeholder: "Proposed treatment plan..." },
  { key: "consent", label: "Consent" },
];

export default function ConsultationFlow() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [c, setC] = useState(null);
  const [step, setStep] = useState(0);
  const [aiLoading, setAiLoading] = useState(false);

  useEffect(() => { api.get("/consultations").then((r) => { const found = r.data.find((x) => x.id === id); setC(found); setStep(found?.step || 0); }); }, [id]);

  if (!c) return <Skeleton className="h-96 rounded-2xl" />;

  const save = async (extra = {}) => {
    const payload = { ...c, ...extra, step };
    setC(payload);
    await api.put(`/consultations/${id}`, payload);
  };

  const next = async () => {
    if (step < STEPS.length - 1) { const ns = step + 1; setStep(ns); await save({ step: ns }); }
    else { await api.put(`/consultations/${id}`, { ...c, status: "completed", step }); toast.success("Consultation completed"); navigate("/consultations"); }
  };

  const aiSuggest = async () => {
    setAiLoading(true);
    try {
      const key = STEPS[step].key;
      const { data } = await api.post("/ai/insight", {
        prompt: `Suggest concise clinical text for the "${STEPS[step].label}" section of a dental implant consultation.`,
        context: { patient: c.patient_name, chief_complaint: c.chief_complaint, findings: c.findings, medical_history: c.medical_history },
      });
      setC({ ...c, [key]: (c[key] ? c[key] + "\n" : "") + data.insight });
      toast.success("AI suggestion added — review & edit");
    } catch { toast.error("AI unavailable"); }
    finally { setAiLoading(false); }
  };

  const current = STEPS[step];

  return (
    <div>
      <PageHeader breadcrumb={["Workspace", "Consultations", c.patient_name]} title={`Consultation — ${c.patient_name}`} subtitle="Guided clinical workflow" />

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        {/* Steps sidebar */}
        <SurfaceCard className="p-4 h-fit lg:sticky lg:top-6">
          <div className="text-[11px] font-bold uppercase tracking-wider text-[#8B9698] mb-3 px-1">Progress</div>
          <div className="space-y-1">
            {STEPS.map((s, i) => (
              <button key={s.key} onClick={() => setStep(i)} data-testid={`consult-step-${i}`}
                className={cn("flex w-full items-center gap-2.5 rounded-lg px-3 py-2 text-left text-sm transition-colors",
                  i === step ? "bg-[#E8F7F0] dark:bg-[#00A85A]/15 text-[#008F4D] dark:text-[#3FD98A] font-semibold" : "text-[#687173] dark:text-[#8B9698] hover:bg-[#F8F9F8] dark:hover:bg-[#0E1A21]")}>
                <span className={cn("flex h-6 w-6 items-center justify-center rounded-full text-[11px] font-bold shrink-0", i < step ? "bg-[#00A85A] text-white" : i === step ? "border-2 border-[#00A85A] text-[#00A85A]" : "border border-[#E2E8F0] dark:border-[#223139] text-[#8B9698]")}>
                  {i < step ? <Check className="h-3 w-3" /> : i + 1}
                </span>
                {s.label}
              </button>
            ))}
          </div>
        </SurfaceCard>

        {/* Editor */}
        <div className="lg:col-span-3">
          <SurfaceCard className="p-6">
            <div className="flex items-center justify-between mb-1">
              <h3 className="text-lg font-bold text-[#101516] dark:text-white">{current.label}</h3>
              <span className="text-xs text-[#8B9698]">Step {step + 1} of {STEPS.length}</span>
            </div>

            {current.key === "consent" ? (
              <div className="mt-5">
                <div className="rounded-xl border border-[#E2E8F0] dark:border-[#223139] p-5">
                  <div className="flex items-start gap-3">
                    <Checkbox checked={c.consent} onCheckedChange={(v) => setC({ ...c, consent: !!v })} data-testid="consent-checkbox" className="mt-0.5" />
                    <div><div className="text-sm font-semibold text-[#101516] dark:text-white">Informed consent obtained</div><p className="text-xs text-[#687173] dark:text-[#8B9698] mt-1">The patient has been informed of the diagnosis, treatment options, risks, and alternatives, and has provided consent to proceed.</p></div>
                  </div>
                </div>
              </div>
            ) : (
              <div className="mt-4">
                <div className="flex justify-end mb-2">
                  <Button onClick={aiSuggest} disabled={aiLoading} size="sm" variant="outline" data-testid="ai-suggest-btn" className="rounded-lg border-[#00A85A]/30 text-[#00A85A] hover:bg-[#E8F7F0]">
                    {aiLoading ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : <Sparkles className="h-3.5 w-3.5 mr-1.5" />}AI Suggest
                  </Button>
                </div>
                <Textarea value={c[current.key] || ""} onChange={(e) => setC({ ...c, [current.key]: e.target.value })} onBlur={() => save()} placeholder={current.placeholder} rows={8} data-testid="consult-textarea" className="resize-none" />
              </div>
            )}

            <div className="mt-6 flex items-center justify-between">
              <Button variant="ghost" onClick={() => setStep(Math.max(0, step - 1))} disabled={step === 0} className="text-[#687173]"><ArrowLeft className="h-4 w-4 mr-1.5" />Back</Button>
              <Button onClick={next} data-testid="consult-next-btn" className="bg-[#00A85A] hover:bg-[#008F4D] text-white rounded-lg">
                {step === STEPS.length - 1 ? "Complete Consultation" : "Continue"}<ArrowRight className="h-4 w-4 ml-1.5" />
              </Button>
            </div>
          </SurfaceCard>
        </div>
      </div>
    </div>
  );
}
